import { cn } from '~/utils/cn';

import type { StatusTone } from '../../constants/status-tones';

/** Tone → pill + dot classes, using the shared semantic design tokens. */
const TONE_STYLE: Record<StatusTone, { pill: string; dot: string }> = {
  info: { pill: 'bg-info-50 text-info', dot: 'bg-info' },
  success: { pill: 'bg-success-50 text-success', dot: 'bg-success' },
  warning: { pill: 'bg-warning-50 text-warning', dot: 'bg-warning' },
  error: { pill: 'bg-error-50 text-error', dot: 'bg-error' },
  muted: { pill: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
};

interface Props {
  /** Semantic colour tone — derive from a status via the `*Tone` maps in status-tones.ts. */
  tone: StatusTone;
  /** Human-readable, translated status label. */
  label: string;
  /** Pulse the dot to signal a live/running state. */
  pulse?: boolean;
  className?: string;
  /** `data-testid` for the pill. */
  testId?: string;
}

/**
 * Rounded status pill (coloured dot + label) for delivery run / product / job
 * statuses. Colour comes from the semantic tone; a pulsing dot marks live runs.
 * Purely presentational — callers map their status enum to a `tone` + `label`.
 */
export function DeliveryStatusChip({
  tone,
  label,
  pulse = false,
  className,
  testId = 'delivery-status-chip',
}: Props) {
  const style = TONE_STYLE[tone];
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-0.5 text-xs font-semibold',
        style.pill,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn('size-1.5 rounded-full', style.dot, pulse && 'animate-pulse')}
      />
      {label}
    </span>
  );
}
